import React, { createContext, useContext, useEffect, useState } from 'react';
import { Store } from '../config/types';
import { fetchStores } from '../services/api';

interface StoresCtx {
  stores: Store[];
  byCode: Map<string, Store>;
  loading: boolean;
  isValid: (code: string | null | undefined) => boolean;
  get:    (code: string) => Store | undefined;
}

const StoresContext = createContext<StoresCtx | undefined>(undefined);

export const StoresProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStores()
      .then(setStores)
      .finally(() => setLoading(false));
  }, []);

  const byCode = React.useMemo(
    () => new Map(stores.map((s) => [s.code, s] as const)),
    [stores]
  );

  const value: StoresCtx = {
    stores,
    byCode,
    loading,
    isValid: (code) => !!code && byCode.has(code),
    get:     (code) => byCode.get(code),
  };

  return <StoresContext.Provider value={value}>{children}</StoresContext.Provider>;
};

export const useStores = () => {
  const ctx = useContext(StoresContext);
  if (!ctx) throw new Error('useStores must be used inside <StoresProvider>');
  return ctx;
};
